import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

import Login from '../pages/Login/Login';
import DashboardUser from '../pages/DashboardUse/DashboardUser';
import DashboardIntervenant from '../pages/DashboardIntervenant/DashboardIntervenant';
import Categories from '../pages/Categories/Categories';
import MainLayout from '../layouts/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import Administrator from '../pages/Adminstrateur/Administrator';
import { Box } from '@mui/material';
import Clients from '../pages/Clients/Clients';
import ClientsAbonnes from '../pages/Clients/ClientsAbonnes';
import ClientsPackJetons from '../pages/Clients/ClientsPackJetons';
import Abonnements from '../pages/Abonnements/Abonnements';
import PacksJetons from '../pages/PacksJetons/PacksJetons';
import Commandes from '../pages/Commandes/Commandes';
import Factures from '../pages/Factures/Factures';

import FacturesHonoraires from '../pages/Factures/FacturesHonoraires';
import Newsletter from '../pages/Contact/Newsletter';
import ContactMessages from '../pages/Contact/ContactMessages';
import AIQuestions from '../pages/Contact/AIQuestions';
import Reservations from '../pages/Reservations/Reservations';
import Offres from '../pages/Offres/Offres';
import Videos from '../pages/Videos/Videos';
import Ateliers from '../pages/Ateliers/Ateliers';
import WorkshopDetails from '../pages/Ateliers/WorkshopDetails';
import Profile from '../pages/Profile/Profile';
import Conversation from '../pages/Messages/Conversation';
import GuideVideos from '../pages/GuideVideos/GuideVideos';
import Statistiques from '../pages/Statistiques/Statistiques';
import EmailsHistory from '../pages/EmailsHistory/EmailsHistory';
// Composant de protection des routes
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" />;

  const userRole = Number(user.role);

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" />;
  }

  return children;
};

// Redirection intelligente selon le rôle
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;

  const role = Number(user.role);
  if (role === 1 || role === 2)
    return <Navigate to="/dashboard-user" replace />;
  if (role === 3) return <Navigate to="/dashboard-intervenant" replace />;

  return <Navigate to="/login" />;
};

const AppRoutes = () => {
  return (
    <Router>
      <Routes>
        {/* 1. Route publique */}
        <Route path="/login" element={<Login />} />

        {/* 2. Routes avec Layout et Protection */}
        <Route
          path="/"
          element={
            <ProtectedRoute allowedRoles={[1, 2, 3]}>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Accueil dynamique */}
          <Route index element={<RoleBasedRedirect />} />

          {/* --- MESSAGES (Sorti du bloc contact pour être accessible via /messages) --- */}
          <Route
            path="messages"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <ContactMessages />
              </ProtectedRoute>
            }
          />

          {/* Administration */}
          <Route
            path="administration"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <Administrator />
              </ProtectedRoute>
            }
          />

          {/* Dashboards */}
          <Route
            path="dashboard-user"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <DashboardUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="dashboard-intervenant"
            element={
              <ProtectedRoute allowedRoles={[3]}>
                <DashboardIntervenant />
              </ProtectedRoute>
            }
          />

          {/* Boutique et Catalogues */}
          <Route
            path="abonnements"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <Abonnements />
              </ProtectedRoute>
            }
          />
          <Route
            path="packs-jetons"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <PacksJetons />
              </ProtectedRoute>
            }
          />
          <Route path="categories" element={<Categories />} />

          {/* Gestion Clients */}
          <Route
            path="clients"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <Clients />
              </ProtectedRoute>
            }
          />
          <Route
            path="clients-abonnes"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <ClientsAbonnes />
              </ProtectedRoute>
            }
          />
          <Route
            path="clients-pack-jetons"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <ClientsPackJetons />
              </ProtectedRoute>
            }
          />

          {/* Commandes et Factures */}
          <Route
            path="commandes"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Commandes />
              </ProtectedRoute>
            }
          />
          <Route
            path="commandes/factures"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Factures />
              </ProtectedRoute>
            }
          />
          <Route
            path="commandes/factures-honoraires"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <FacturesHonoraires />
              </ProtectedRoute>
            }
          />

          {/* Autres options de contact */}
          <Route path="contact">
            <Route
              path="newsletter"
              element={
                <ProtectedRoute allowedRoles={[1, 2]}>
                  <Newsletter />
                </ProtectedRoute>
              }
            />
            <Route
              path="messages"
              element={
                <ProtectedRoute allowedRoles={[1, 2]}>
                  <ContactMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="questions-ai"
              element={
                <ProtectedRoute allowedRoles={[1, 2]}>
                  <AIQuestions />
                </ProtectedRoute>
              }
            />
          </Route>
          {/* Gestion des Offres */}
          <Route
            path="offres"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Offres />
              </ProtectedRoute>
            }
          />
          {/* Gestion des Réservations */}
          <Route
            path="reservations"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Reservations />
              </ProtectedRoute>
            }
          />

          <Route
            path="videos"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Videos />
              </ProtectedRoute>
            }
          />
          <Route
            path="guide-videos"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <GuideVideos />
              </ProtectedRoute>
            }
          />
          {/* Gestion des Ateliers */}
          <Route
            path="ateliers"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Ateliers />
              </ProtectedRoute>
            }
          />
          <Route path="/ateliers/:id" element={<WorkshopDetails />} />
          {/* Conversations */}
          <Route
            path="conversations"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Conversation />
              </ProtectedRoute>
            }
          />

          <Route
            path="statistiques"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <Statistiques />
              </ProtectedRoute>
            }
          />
        
          <Route
            path="emails-history"
            element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <EmailsHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="mon-profil"
            element={
              <ProtectedRoute allowedRoles={[1, 2, 3]}>
                <Profile />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* 3. Sécurités et Erreurs */}
        <Route
          path="/unauthorized"
          element={
            <Box sx={{ p: 5 }}>
              <h1>Accès interdit</h1>
            </Box>
          }
        />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
